import { If, onCleanup, onMount } from 'cdui-js';
import i18n from '../../../i18n';
import { initRNBridge, registerRNHandler } from '../../../lib/rnBridge';
import {
  applyFundRecordsShell,
  bootFundRecordsApp,
  getBackToAppUrl,
  isFundBackToApp,
  isFundHideNav,
  leaveFundRecords,
} from '../deposit/h5-deposit/appBridge';
import { IconBack } from '../deposit/h5-deposit/icons';
import { recordDetailOf } from './shared';

type ShellProps = {
  right?: any;
  footer?: any;
  children?: any;
};

export const RecordShell = (props: ShellProps) => {
  applyFundRecordsShell();

  /** 详情页（/h5/fund-records/{kind}/{id}）返回行为是回列表页，不回 App；
   *  只有列表页（/h5/fund-records）才走 isBackToApp=1 的 leaveToApp 逻辑。
   *  detailUrl 会透传 isBackToApp=1 到详情 URL，但详情页不让它生效，避免详情返回直接跳 App。 */
  const isListPage = !recordDetailOf();
  const backToApp = isFundBackToApp();
  /** 列表页 isBackToApp=1 时 leaveFundRecords → leaveToApp 关 WebView（带 backToAppUrl），不垫 history，避免 iOS 空退；
   *  详情页走 history.back 回列表页。 */
  const handleBack = () => {
    if (isListPage && backToApp) {
      leaveFundRecords();
    } else {
      window.history.back();
    }
  };

  onMount(() => {
    void bootFundRecordsApp();
    initRNBridge();
    console.log('[fund-records] 进入资金记录页', {
      url: window.location.href,
      isListPage,
      isBackToApp: backToApp,
      backToAppUrl: getBackToAppUrl(),
      willBlockBack: backToApp && isListPage,
    });
    const unreg = registerRNHandler((msg) => {
      if (msg?.type !== 'APP_TO_WEB_GoBack') return;
      handleBack();
    });

    /**
     * iOS 系统侧滑/物理回退不经过 RN 桥也不经过按钮 onclick，直接触发 popstate 路由栈回退。
     * 列表页 isBackToApp=1 时用 pushState 垫一条同 URL 的占位条目吸收系统回退，popstate 触发时调
     * leaveFundRecords（→ leaveToApp → postToApp，不做 history.back，不会把 URL 退回上一页）：
     *   - 有 backToAppUrl → postToApp(WEB_TO_APP_OpenNative) 回证券相应页
     *   - 无 backToAppUrl → goBackByBrowser 退上一页
     * 详情页不挂 popstate 监听，让浏览器自然 history.back 回列表页。
     * 不能用 bindOverlayHistory：它的 onBack 走 leaveFundRecords，无 backToAppUrl 时 goBackByBrowser
     * 会 history.back 把 URL 退回入金表单，导致 List 的 pickAccount 拿不到 URL 上的 accountNum。
     */
    let unblock: (() => void) | undefined;
    if (backToApp && isListPage) {
      let armed = false;
      const onPopState = () => {
        console.log('[fund-records] popstate 触发', {
          armed,
          url: window.location.href,
          isBackToApp: isFundBackToApp(),
          backToAppUrl: getBackToAppUrl(),
        });
        if (!armed) return; // 忽略 pushState 自身可能触发的伪 popstate
        armed = false;
        window.removeEventListener('popstate', onPopState);
        leaveFundRecords();
      };
      window.addEventListener('popstate', onPopState);
      window.history.pushState({ __difiFundRecordsBack: 1 }, '', window.location.href);
      console.log('[fund-records] pushState guard 已推', {
        state: window.history.state,
        url: window.location.href,
      });
      // pushState 后下一个 tick 再 arm，避免某些 WebView 在 pushState 后立即抛 popstate 的 quirk
      setTimeout(() => {
        armed = true;
        console.log('[fund-records] popstate 监听已 arm');
      }, 0);
      unblock = () => {
        armed = false;
        window.removeEventListener('popstate', onPopState);
      };
    } else {
      console.log('[fund-records] 未挂 popstate 监听', { isListPage, isBackToApp: backToApp });
    }

    onCleanup(() => {
      unreg();
      unblock?.();
    });
  });

  const hideNav = isFundHideNav();

  return (
    <div class="deposit-records">
      <If when={!hideNav}>
        <div class="deposit-records-nav">
          <button type="button" class="deposit-records-back" onclick={() => handleBack()}>
            <IconBack />
          </button>
          <div class="deposit-records-title">{i18n.FundRecords.Title}</div>
          <div class="deposit-records-nav-right">{props.right}</div>
        </div>
      </If>
      {props.children}
      {props.footer}
    </div>
  );
};
